import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import Login from "../features/auth/pages/LoginPage";
import EmployeeRegisterPage from "../features/auth/pages/employeeRegister/EmployeeRegisterPage";
import MainLandingPage from "../features/marketingMain/pages/MainLandingPage";
import PrivacyPolicyPage from "../features/legal/pages/PrivacyPolicyPage";

import NotFound from "../pages/NotFound";
import NotFoundMenu from "../features/notFound/pages/NotFoundMenuPage";

import Menu from "../features/layout/components/MenuLayout";
import TenantDashboardPage from "../features/fleetops/pages/TenantDashboardPage";
import Products from "../features/products/pages/ProductsPage";
import Employees from "../features/employees/pages/EmployeesPage";
import PotencialClients from "../features/potentialClients/pages/PotentialClientsPage";
import Logs from "../features/logs/pages/LogsPage";
import Settings from "../features/settings/pages/SettingsPage";
import SettingsProducts from "../features/categoryManager/pages/CategoryManagerPage";

import SalesContactImportPage from "../features/sales/pages/contactImportPage/ContactImportPage";
import SalesContactsPage from "../features/sales/pages/contactPage/ContactsPage";
import SalesLeadsPage from "../features/sales/pages/leadsPage/LeadsPage";
import SalesDealsHubPage from "../features/sales/pages/deals/DealsHubPage";
import SalesTasksPage from "../features/sales/pages/tasksPage/TasksPage";
import SalesSourcesPage from "../features/sales/pages/sourcesPage/SourcesPage";
import SalesPipelinePage from "../features/sales/pages/pipeline/PipelinePage";
import SalesActivitiesPage from "../features/sales/pages/activities/ActivitiesPage";
import PersonalDetails from "../features/sales/pages/personalDetails/PersonalDetails";

import CRMSettingsPage from "../features/settings/pages/crmSettings/CRMSettingsPage";
import WorkSchedulePage from "../features/workSchedule/pages/WorkSchedulePage";
import ShopSettingsPage from "../features/settings/pages/shopSettings/ShopSettingsPage";
import PermissionsPage from "../features/settings/pages/permissions/PermissionsPage";
import TenantControlPage from "../features/settings/pages/tenantControl/TenantControlPage";
import TelegramBotSettingsPage from "../features/settings/pages/telegramBotSettings/TelegramBotSettingsPage";
import TelegramConnectionsPage from "../features/settings/pages/telegramConnections/TelegramConnectionsPage";

import ChatPage from "../features/chat/pages/ChatPage";
import AccountPage from "../features/account/pages/AccountPage";
import MarketingPage from "../features/marketing/pages/MarketingPage";
import AcademyPage from "../features/academy/pages/AcademyPage";
import FleetControlPage from "../features/fleetops/pages/FleetControlPage";
import InvoicesPage from "../features/fleetops/pages/InvoicesPage";
import InvoiceAnalyticsPage from "../features/fleetops/pages/InvoiceAnalyticsPage";
import InvoiceSyncSettingsPage from "../features/fleetops/pages/InvoiceSyncSettingsPage";

import { RateLimitingSettings, RateLimitStats } from "../components/RateLimit";

import { isAuthenticated, getUserRole } from "../shared/lib/auth/authHelper";
import PermissionRoute from "../shared/components/PermissionRoute";
import { useTenantBranding } from "../shared/branding/TenantBrandingProvider";
import { useTranslation } from "react-i18next";
import AnalyticsConsentBanner from "../shared/analytics/AnalyticsConsentBanner";
import SeoManager from "../shared/seo/SeoManager";

function PublicLoadingScreen() {
  const { t } = useTranslation();
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--tenant-login-bg, #2575fc)",
        color: "#fff",
        fontSize: "18px",
        fontWeight: 600,
      }}
    >
      {t("uploading")}
    </div>
  );
}

function PublicHome() {
  const { branding, isLoaded } = useTenantBranding();

  if (!isLoaded) {
    return <PublicLoadingScreen />;
  }

  if (branding?.is_main) {
    return <MainLandingPage />;
  }

  return <Login />;
}

function AppRoutes() {
  const [authenticated, setAuthenticated] = useState(() => isAuthenticated());
  const { branding, isLoaded } = useTenantBranding();
  const isSuperAdmin = String(getUserRole() || "").toLowerCase() === "superadmin";

  const allowPublicEmployeeRegistration =
    branding?.allow_public_employee_registration !== false;

  useEffect(() => {
    const syncAuth = () => {
      setAuthenticated(isAuthenticated());
    };

    window.addEventListener("storage", syncAuth);
    window.addEventListener("token-changed", syncAuth);
    window.addEventListener("permissions-updated", syncAuth);

    return () => {
      window.removeEventListener("storage", syncAuth);
      window.removeEventListener("token-changed", syncAuth);
      window.removeEventListener("permissions-updated", syncAuth);
    };
  }, []);

  if (!isLoaded) {
    return <PublicLoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicHome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />

      <Route
        path="/employee-register"
        element={
          allowPublicEmployeeRegistration ? <EmployeeRegisterPage /> : <NotFound />
        }
      />

      <Route path="/menu/*" element={authenticated ? <Menu /> : <Login />}>
        <Route
          path="dashboard"
          element={
            <PermissionRoute permission="dashboard.view">
              <TenantDashboardPage />
            </PermissionRoute>
          }
        />

        <Route path="academy" element={<AcademyPage />} />
        <Route path="fleet" element={<PermissionRoute permission="crm_settings.view"><FleetControlPage /></PermissionRoute>} />
        <Route path="invoices" element={<PermissionRoute permission="crm_settings.view"><InvoicesPage /></PermissionRoute>} />
        <Route path="invoice-analytics" element={<PermissionRoute permission="crm_settings.view"><InvoiceAnalyticsPage /></PermissionRoute>} />

        <Route
          path="products"
          element={
            <PermissionRoute permission="products.view">
              <Products />
            </PermissionRoute>
          }
        />

        <Route
          path="employees"
          element={
            <PermissionRoute permission="users.view">
              <Employees />
            </PermissionRoute>
          }
        />

        <Route
          path="potential-clients"
          element={
            <PermissionRoute permission="companies.view">
              <PotencialClients />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/contacts/import"
          element={
            <PermissionRoute permission="contacts.import">
              <SalesContactImportPage />
            </PermissionRoute>
          }
        />
        <Route
          path="sales/contacts"
          element={
            <PermissionRoute permission="contacts.view">
              <SalesContactsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/leads"
          element={
            <PermissionRoute permission="leads.view">
              <SalesLeadsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/deals"
          element={
            <PermissionRoute permission="deals.view">
              <SalesDealsHubPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/tasks"
          element={
            <PermissionRoute permission="tasks.view">
              <SalesTasksPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/sources"
          element={
            <PermissionRoute permission="sources.view">
              <SalesSourcesPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/pipeline"
          element={
            <PermissionRoute permission="pipeline.view">
              <SalesPipelinePage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/activities"
          element={
            <PermissionRoute permission="activities.view">
              <SalesActivitiesPage />
            </PermissionRoute>
          }
        />

        <Route
          path="sales/contacts/:id/personal-details"
          element={
            <PermissionRoute permission="contacts.view">
              <PersonalDetails />
            </PermissionRoute>
          }
        />

        <Route
          path="marketing"
          element={
            <PermissionRoute permission="marketing.view">
              <MarketingPage />
            </PermissionRoute>
          }
        />

        <Route
          path="chat"
          element={
            <PermissionRoute permission="chat.view">
              <ChatPage />
            </PermissionRoute>
          }
        />

        <Route path="account" element={<AccountPage />} />

        <Route
          path="logs"
          element={
            <PermissionRoute permission="activity_logs.view">
              <Logs />
            </PermissionRoute>
          }
        />

        <Route path="settings" element={<Settings />} />

        <Route
          path="settings/products"
          element={
            <PermissionRoute
              anyPermissions={[
                "categories.view",
                "sub_categories.view",
                "products.view",
              ]}
            >
              <SettingsProducts />
            </PermissionRoute>
          }
        />

        <Route
          path="settings/crm"
          element={
            <PermissionRoute permission="crm_settings.view">
              <CRMSettingsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="work-schedule"
          element={
            <PermissionRoute permission="work_schedule.view">
              <WorkSchedulePage />
            </PermissionRoute>
          }
        />

        <Route
          path="settings/shop"
          element={
            <PermissionRoute permission="crm_settings.view">
              <ShopSettingsPage />
            </PermissionRoute>
          }
        />


        <Route
          path="settings/invoice-sync"
          element={
            <PermissionRoute permission="crm_settings.view">
              <InvoiceSyncSettingsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="settings/telegram"
          element={
            <PermissionRoute permission="crm_settings.view">
              <TelegramBotSettingsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="settings/telegram-connections"
          element={
            <PermissionRoute permission="crm_settings.view">
              <TelegramConnectionsPage />
            </PermissionRoute>
          }
        />

        <Route
          path="settings/tenants"
          element={
            branding?.is_main ? (
              <PermissionRoute permission="crm_settings.view">
                <TenantControlPage />
              </PermissionRoute>
            ) : (
              <NotFoundMenu />
            )
          }
        />

        <Route
          path="settings/permissions"
          element={
            isSuperAdmin ? (
              <PermissionsPage />
            ) : (
              <PermissionRoute
                anyPermissions={[
                  "permissions.view",
                  "roles.view",
                  "role_permissions.view",
                ]}
              >
                <PermissionsPage />
              </PermissionRoute>
            )
          }
        />

        <Route
          path="admin/rate-limiting"
          element={
            <PermissionRoute permission="system.rate_limits.update">
              <RateLimitingSettings />
            </PermissionRoute>
          }
        />

        <Route
          path="admin/rate-limiting/stats"
          element={
            <PermissionRoute permission="system.rate_limits.view">
              <RateLimitStats />
            </PermissionRoute>
          }
        />

        <Route path="*" element={<NotFoundMenu />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function AppRouter() {
  return (
    <Router>
      <SeoManager />
      <AnalyticsConsentBanner />
      <AppRoutes />
    </Router>
  );
}
